/**
 * Real-time Notification Status Component
 * Shows connection status and provides real-time notifications
 */

import React, { useState, useEffect } from "react";
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Button,
  Alert,
} from "@mui/material";
import {
  Notifications,
  NotificationsActive,
  NotificationsOff,
  Wifi,
  WifiOff,
  Circle,
  Assignment,
  Update,
  CheckCircle,
  Inventory,
  Refresh,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useRealTimeNotifications } from "../../hooks/useRealTimeNotifications";

console.log("[DEBUG] RealTimeNotificationStatus module loaded");

const RealTimeNotificationStatus = ({ token, onNotificationClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showConnectionStatus, setShowConnectionStatus] = useState(false);

  console.log(
    "[RealTimeNotificationStatus] Component rendered with token:",
    token ? "Token present" : "No token"
  );

  const {
    isConnected,
    connectionError,
    connectionStatus,
    notifications,
    unreadCount,
    lastNotification,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
  } = useRealTimeNotifications(token);

  const isMenuOpen = Boolean(anchorEl);

  // Auto-hide connection status after 5 seconds
  useEffect(() => {
    if (connectionStatus === "connected") {
      setShowConnectionStatus(true);
      const timer = setTimeout(() => {
        setShowConnectionStatus(false);
      }, 3000);
      return () => clearTimeout(timer);
    } else if (connectionStatus === "error" || connectionStatus === "failed") {
      setShowConnectionStatus(true);
    }
  }, [connectionStatus]);

  const handleNotificationsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationItemClick = (notification) => {
    markAsRead(notification.id);
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    handleNotificationsClose();
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "task-assignment":
        return <Assignment color="primary" />;
      case "status-update":
        return <Update color="info" />;
      case "request-completed":
        return <CheckCircle color="success" />;
      case "inventory-alert":
        return <Inventory color="warning" />;
      case "maintenance-request":
        return <Assignment color="secondary" />;
      default:
        return <Notifications color="action" />;
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case "connected":
        return "success";
      case "disconnected":
        return "default";
      case "error":
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case "connected":
        return "Real-time notifications active";
      case "disconnected":
        return "Connecting to notification server...";
      case "error":
        return `Connection error: ${connectionError}`;
      case "failed":
        return "Failed to connect to notification server";
      default:
        return "Initializing...";
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Connection Status Indicator */}
      <Tooltip title={getConnectionStatusText()}>
        <IconButton size="small">
          {isConnected ? (
            <Wifi color="success" fontSize="small" />
          ) : (
            <WifiOff color="error" fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      {/* Notification Bell */}
      <Tooltip title={`${unreadCount} unread notifications`}>
        <IconButton
          color="inherit"
          onClick={handleNotificationsClick}
          sx={{
            animation:
              lastNotification && unreadCount > 0
                ? "pulse 2s infinite"
                : "none",
            "@keyframes pulse": {
              "0%": {
                transform: "scale(1)",
              },
              "50%": {
                transform: "scale(1.1)",
              },
              "100%": {
                transform: "scale(1)",
              },
            },
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? (
              <NotificationsActive />
            ) : isConnected ? (
              <Notifications />
            ) : (
              <NotificationsOff />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Connection Status Alert */}
      {showConnectionStatus && (
        <Alert
          severity={getConnectionStatusColor()}
          variant="filled"
          sx={{
            position: "fixed",
            top: 80,
            right: 16,
            zIndex: 1300,
            minWidth: 300,
          }}
          onClose={() => setShowConnectionStatus(false)}
        >
          {getConnectionStatusText()}
        </Alert>
      )}

      {/* Notifications Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleNotificationsClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            overflow: "hidden",
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              Notifications
              {unreadCount > 0 && (
                <Chip
                  label={unreadCount}
                  color="error"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Box>
              <Tooltip title="Refresh notifications">
                <IconButton size="small" onClick={refreshNotifications}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllRead} sx={{ ml: 1 }}>
                  Mark all read
                </Button>
              )}
            </Box>
          </Box>

          {/* Connection Status */}
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <Circle
              sx={{
                fontSize: 8,
                color: isConnected ? "success.main" : "error.main",
                mr: 1,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              {isConnected
                ? "Real-time updates active"
                : "Offline - updates paused"}
            </Typography>
          </Box>
        </Box>

        {/* Notifications List */}
        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="textSecondary">No notifications</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.slice(0, 10).map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationItemClick(notification)}
                    sx={{
                      bgcolor: notification.read
                        ? "transparent"
                        : "action.hover",
                      borderLeft: !notification.read ? 3 : 0,
                      borderColor: "primary.main",
                      "&:hover": {
                        bgcolor: "action.selected",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getNotificationIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="subtitle2">
                            {notification.title}
                          </Typography>
                          {!notification.read && (
                            <Circle
                              sx={{
                                fontSize: 8,
                                color: "primary.main",
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            color="textPrimary"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {notification.createdAt &&
                              formatDistanceToNow(
                                new Date(notification.createdAt),
                                {
                                  addSuffix: true,
                                }
                              )}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 10 && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Typography variant="caption" color="textSecondary">
              Showing 10 of {notifications.length} notifications
            </Typography>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default RealTimeNotificationStatus;
